import type { OfferingType } from "@/components/map/types";
import { LEGAL_ENTITY, LINKEDIN_PAGES } from "@landvest/legal-entity";
import type { TeamMemberKey } from "@landvest/team";
import { LANDVEST_HOST } from "@oekopunktemarkt/entity-pointers";

/**
 * Internal tenant identifiers (abstract, stable)
 */
export type TenantId =
  | "main" // euro-pro-flaeche.de - all offerings
  | "okopunkte" // oekopunktemarkt.de
  | "co2" // co2-markt.de
  | "jagd" // jagdpachtmarkt.de
  | "energie" // EE-Pachtmarkt.de (renewable energy)
  | "acker"; // Ackerpachtmarkt.de (agriculture)

/**
 * Tenant configuration interface
 */
export interface TenantConfig {
  id: TenantId;
  name: string;
  tagline: string;
  domain: string;
  // Which offering types this tenant focuses on
  offeringTypes: OfferingType[];
  // Brand colors
  theme: {
    primary: string;
    primaryForeground: string;
    accent: string;
  };
  // Logo configuration
  logo: {
    icon: string; // Lucide icon name or custom SVG
    image?: string; // Optional path to logo image in public folder
    text: string;
    textSuffix?: string;
  };
  // Feature flags
  features: {
    showAllOfferings: boolean; // Show all types or just tenant-specific
    showModeToggle: boolean; // Show landowner/buyer toggle
    enableRegistration: boolean;
    isPlatformHub: boolean; // True for main tenant (euro-pro-flaeche.de)
  };
  // UI labels (tenant-specific wording)
  labels: {
    landownerMode: string; // e.g., "Ich habe Land"
    landownerModeShort: string; // e.g., "Anbieten"
    buyerMode: string; // e.g., "Ich brauche Punkte"
    buyerModeShort: string; // e.g., "Suchen"
    createOfferingButton: string; // e.g., "Angebot erstellen"
  };
  // SEO
  seo: {
    title: string;
    description: string;
    /**
     * Corporate facts for the site-wide Organization JSON-LD. Declared only
     * for tenants whose lineage is verified — corporate structure is a fact
     * to confirm, never plausible filler (.agents/rules/naming.md §3). A
     * tenant without this block publishes a plain Organization.
     */
    organization?: {
      /** Public profiles for schema.org `sameAs`. */
      sameAs?: readonly string[];
      /** Founders of the platform, by @landvest/team key, in the order to publish. */
      founders?: readonly TeamMemberKey[];
      /** The operating company, when it is a different legal entity from the brand. */
      parentOrganization?: { name: string; url: string };
    };
  };
}

/**
 * All tenant configurations
 */
export const TENANTS: Record<TenantId, TenantConfig> = {
  main: {
    id: "main",
    name: "Euro Pro Fläche",
    tagline: "Der Marktplatz für Ihre Flächen",
    domain: "euro-pro-flaeche.de",
    offeringTypes: [
      "okopunkte",
      "ausgleichsflaechen",
      "landverpachtung",
      "co2_zertifikate",
      "jagdpachten",
    ],
    theme: {
      primary: "#283c24",
      primaryForeground: "#ffffff",
      accent: "#307c34",
    },
    logo: {
      icon: "layers",
      text: "Euro Pro Fläche",
    },
    features: {
      showAllOfferings: true,
      showModeToggle: true,
      enableRegistration: true,
      isPlatformHub: true,
    },
    labels: {
      landownerMode: "Ich habe Punkte",
      landownerModeShort: "Anbieten",
      buyerMode: "Ich suche Angebote",
      buyerModeShort: "Suchen",
      createOfferingButton: "Angebot erstellen",
    },
    seo: {
      title: "Euro Pro Fläche - Der Marktplatz für Ihre Flächen",
      description:
        "Finden Sie Ökopunkte, Ausgleichsflächen, Landpacht, CO2-Zertifikate und Jagdpachten auf einem Marktplatz.",
    },
  },
  okopunkte: {
    id: "okopunkte",
    name: "Ökopunktemarkt",
    tagline: "Ökopunkte kaufen und verkaufen",
    domain: "oekopunktemarkt.de",
    offeringTypes: ["okopunkte", "ausgleichsflaechen"],
    theme: {
      primary: "#132610",
      primaryForeground: "#ffffff",
      accent: "#03b75f",
    },
    logo: {
      icon: "leaf",
      image: "/oekopunktemarkt-mark.svg",
      text: "Ökopunktemarkt",
    },
    features: {
      showAllOfferings: false,
      showModeToggle: true,
      enableRegistration: true,
      isPlatformHub: false,
    },
    labels: {
      landownerMode: "Ich habe Punkte",
      landownerModeShort: "Anbieten",
      buyerMode: "Ich brauche Punkte",
      buyerModeShort: "Suchen",
      createOfferingButton: "Kompensation anbieten",
    },
    seo: {
      // No "kaufen": the money term belongs to /oekopunkte-kaufen, and this
      // title is the homepage's and every title-less route's fallback.
      title: "Ökopunktemarkt.de - Marktplatz für Ökopunkte und Kompensation",
      // ARCH-033: Ökopunktemarkt ist ein Teil der LandVest GmbH; founded 2019
      // by Anja Ziolko and Maximilian Prinz zu Sayn-Wittgenstein-Berleburg
      // (order as on /ueber-uns). The LinkedIn page is the one the Impressum
      // names as our Social-Media-Profil.
      organization: {
        sameAs: [LINKEDIN_PAGES.oekopunktemarkt],
        founders: ["anja", "max"],
        parentOrganization: { name: LEGAL_ENTITY.name, url: LANDVEST_HOST },
      },
      // Site-WIDE default: the root layout falls back to this for every route
      // that sets no description of its own, and it is also the Organization /
      // WebSite JSON-LD description on every URL. So it must stay generic —
      // money-term copy belongs on the page that owns the term (app/page.tsx),
      // not repeated across ~200 URLs.
      description:
        "Der Marktplatz für Ökopunkte und Ausgleichsflächen in Deutschland. Finden Sie passende Angebote oder bieten Sie Ihre Ökopunkte an.",
    },
  },
  co2: {
    id: "co2",
    name: "CO2-Markt",
    tagline: "CO2-Zertifikate handeln",
    domain: "co2-markt.de",
    offeringTypes: ["co2_zertifikate"],
    theme: {
      primary: "#2196F3",
      primaryForeground: "#ffffff",
      accent: "#03A9F4",
    },
    logo: {
      icon: "cloud",
      text: "CO2-Markt",
      textSuffix: ".de",
    },
    features: {
      showAllOfferings: false,
      showModeToggle: true,
      enableRegistration: true,
      isPlatformHub: false,
    },
    labels: {
      landownerMode: "Ich biete Zertifikate",
      landownerModeShort: "Anbieten",
      buyerMode: "Ich suche Zertifikate",
      buyerModeShort: "Suchen",
      createOfferingButton: "Zertifikate anbieten",
    },
    seo: {
      title: "CO2-Markt.de - CO2-Zertifikate handeln",
      description:
        "Kaufen und verkaufen Sie CO2-Zertifikate auf dem deutschen Marktplatz für Emissionszertifikate.",
    },
  },
  jagd: {
    id: "jagd",
    name: "Jagdpachtmarkt",
    tagline: "Jagdreviere finden und verpachten",
    domain: "jagdpachtmarkt.de",
    offeringTypes: ["jagdpachten"],
    theme: {
      primary: "#795548",
      primaryForeground: "#ffffff",
      accent: "#8D6E63",
    },
    logo: {
      icon: "target",
      text: "Jagdpachtmarkt",
      textSuffix: ".de",
    },
    features: {
      showAllOfferings: false,
      showModeToggle: true,
      enableRegistration: true,
      isPlatformHub: false,
    },
    labels: {
      landownerMode: "Ich verpachte Jagdreviere",
      landownerModeShort: "Verpachten",
      buyerMode: "Ich suche Jagdrevier",
      buyerModeShort: "Suchen",
      createOfferingButton: "Jagdrevier anbieten",
    },
    seo: {
      title: "Jagdpachtmarkt.de - Jagdreviere finden und verpachten",
      description:
        "Der Marktplatz für Jagdpachten in Deutschland. Finden Sie Ihr Jagdrevier oder verpachten Sie Ihre Flächen.",
    },
  },
  energie: {
    id: "energie",
    name: "EE-Pachtmarkt",
    tagline: "Flächen für erneuerbare Energien",
    domain: "ee-pachtmarkt.de",
    offeringTypes: ["landverpachtung"],
    theme: {
      primary: "#FF9800",
      primaryForeground: "#ffffff",
      accent: "#FFC107",
    },
    logo: {
      icon: "sun",
      text: "EE-Pachtmarkt",
      textSuffix: ".de",
    },
    features: {
      showAllOfferings: false,
      showModeToggle: true,
      enableRegistration: true,
      isPlatformHub: false,
    },
    labels: {
      landownerMode: "Ich habe Flächen",
      landownerModeShort: "Anbieten",
      buyerMode: "Ich suche Flächen",
      buyerModeShort: "Suchen",
      createOfferingButton: "Fläche anbieten",
    },
    seo: {
      title: "EE-Pachtmarkt.de - Flächen für erneuerbare Energien",
      description:
        "Verpachten Sie Ihre Flächen für Solar- und Windenergie. Der Marktplatz für Energiepacht in Deutschland.",
    },
  },
  acker: {
    id: "acker",
    name: "Ackerpachtmarkt",
    tagline: "Landwirtschaftliche Flächen pachten",
    domain: "ackerpachtmarkt.de",
    offeringTypes: ["landverpachtung"],
    theme: {
      primary: "#8BC34A",
      primaryForeground: "#ffffff",
      accent: "#CDDC39",
    },
    logo: {
      icon: "wheat",
      text: "Ackerpachtmarkt",
      textSuffix: ".de",
    },
    features: {
      showAllOfferings: false,
      showModeToggle: true,
      enableRegistration: true,
      isPlatformHub: false,
    },
    labels: {
      landownerMode: "Ich verpachte Land",
      landownerModeShort: "Verpachten",
      buyerMode: "Ich suche Pachtland",
      buyerModeShort: "Suchen",
      createOfferingButton: "Land anbieten",
    },
    seo: {
      title: "Ackerpachtmarkt.de - Landwirtschaftliche Flächen pachten",
      description:
        "Der Marktplatz für landwirtschaftliche Pachtflächen. Finden Sie Ackerland zur Pacht in Ihrer Region.",
    },
  },
};

/**
 * Domain to tenant mapping
 */
export const DOMAIN_TO_TENANT: Record<string, TenantId> = {
  // Production domains
  "euro-pro-flaeche.de": "main",
  "www.euro-pro-flaeche.de": "main",
  "oekopunktemarkt.de": "okopunkte",
  "www.oekopunktemarkt.de": "okopunkte",
  "co2-markt.de": "co2",
  "www.co2-markt.de": "co2",
  "jagdpachtmarkt.de": "jagd",
  "www.jagdpachtmarkt.de": "jagd",
  "ee-pachtmarkt.de": "energie",
  "www.ee-pachtmarkt.de": "energie",
  "ackerpachtmarkt.de": "acker",
  "www.ackerpachtmarkt.de": "acker",
  // Local development domains (add to /etc/hosts)
  "oekopunktemarkt.dev": "okopunkte",
  "co2-markt.dev": "co2",
  "jagdpachtmarkt.dev": "jagd",
  "ee-pachtmarkt.dev": "energie",
  "ackerpachtmarkt.dev": "acker",
  "euro-pro-flaeche.dev": "main",
  // Development fallbacks
  localhost: "okopunkte",
  "127.0.0.1": "okopunkte",
  // Staging environments
  "oekopunktemarkt.greenzero.eu": "okopunkte",
  "staging.oekopunktemarkt.de": "okopunkte",
};

/**
 * Get tenant config by ID
 */
export function getTenantById(id: TenantId): TenantConfig {
  return TENANTS[id];
}

/**
 * Get tenant config by domain
 */
export function getTenantByDomain(domain: string): TenantConfig {
  // Strip port if present
  const cleanDomain = domain.split(":")[0].toLowerCase();

  // Check direct domain mapping first
  if (DOMAIN_TO_TENANT[cleanDomain]) {
    return TENANTS[DOMAIN_TO_TENANT[cleanDomain]];
  }

  // Handle Vercel preview domains (e.g., oekopunktemarkt-xxx.vercel.app)
  if (cleanDomain.endsWith(".vercel.app")) {
    // Extract the project name prefix and try to match tenant
    const prefix = cleanDomain.split("-")[0];
    if (prefix.includes("oekopunkte")) return TENANTS.okopunkte;
    if (prefix.includes("co2")) return TENANTS.co2;
    if (prefix.includes("jagd")) return TENANTS.jagd;
    if (prefix.includes("energie") || prefix.includes("ee")) return TENANTS.energie;
    if (prefix.includes("acker")) return TENANTS.acker;
  }

  // Default to okopunkte (most common tenant) for unknown domains
  return TENANTS[DEFAULT_TENANT_ID];
}

/**
 * Default tenant for development
 */
export const DEFAULT_TENANT_ID: TenantId = "okopunkte";

/**
 * Get default tenant
 */
export function getDefaultTenant(): TenantConfig {
  return TENANTS[DEFAULT_TENANT_ID];
}

/**
 * Type guard for TenantId
 */
export function isTenantId(value: string): value is TenantId {
  return value in TENANTS;
}

/**
 * Cookie name for dev-mode tenant override.
 * Used by proxy.ts (to set/read) and layouts (to read).
 * Only active when NODE_ENV !== 'production'.
 */
export const DEV_TENANT_COOKIE = "__dev_tenant";
